import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  Bell, MapPin, Calendar, FileText, ChevronRight, Clock,
  Bookmark, Sparkles, Loader2, Lock,
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { notify } from '../utils/notifications';
import { avatarImageUrl } from '../utils/avatarImage';
import { useApp } from '../context/AppContext';
import { useNotifications } from '../context/NotificationContext';
import { useEventData } from '../context/EventDataContext';
import { useFavorites } from '../hooks/useFavorites';
import { useEventModules } from '../hooks/useEventModules';
import { useEventHomeLayout } from '../hooks/useEventHomeLayout';
import { formatEventDateRange, getScheduleState } from '../utils/scheduleTime';
import { buildHomeDataPlan } from './home/homeDataPlan';
import { HomeQuickActions } from './home/HomeQuickActions';
import { HomeFeaturedCollection } from './home/HomeFeaturedCollection';
import CustomHomeSectionCard from './home/CustomHomeSectionCard';
import { selectHomeAnnouncements } from './home/homeAnnouncements';
import type { ConferenceEvent, EventPartner, Exhibitor, Paper, Session, Profile } from '@conference/shared';

function timeLabel(session: Session): string {
  const fmt = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
  };
  return fmt(session.start_time);
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const { profile, currentEventId, isCurrentEventMember } = useApp();
  const { services: { eventService, exhibitorService, paperService, partnerService, sessionService, speakerService }, load } = useEventData();
  const { notifications: announcements, unreadCount: unreadNotificationCount } = useNotifications();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { isEnabled, labelFor, isReady: areModulesReady } = useEventModules();
  const { blocks: homeBlocks, customSections = [], isLegacy: isLegacyHome, isReady: isHomeReady } = useEventHomeLayout();
  const homeDataPlan = useMemo(() => buildHomeDataPlan(homeBlocks), [homeBlocks]);
  const loadSessions = homeDataPlan.sessions;
  const loadSpeakers = homeDataPlan.speakers;
  const loadPapers = homeDataPlan.papers;
  const loadExhibitors = homeDataPlan.exhibitors;
  const agendaEnabled = isEnabled('agenda');
  const speakersEnabled = isEnabled('speakers');
  const usesFeaturedSponsorSection = customSections.some((section) => section.content_style === 'featured_sponsors');

  const [event, setEvent] = useState<ConferenceEvent | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [speakers, setSpeakers] = useState<Profile[]>([]);
  const [papers, setPapers] = useState<Paper[]>([]);
  const [exhibitors, setExhibitors] = useState<Exhibitor[]>([]);
  const [featuredSponsors, setFeaturedSponsors] = useState<EventPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());
  const loadSequence = useRef(0);

  useEffect(() => {
    if (currentEventId && areModulesReady && isHomeReady) {
      void loadData(
        currentEventId,
        isLegacyHome ? agendaEnabled : loadSessions,
        isLegacyHome ? speakersEnabled : loadSpeakers,
        !isLegacyHome && loadPapers,
        !isLegacyHome && loadExhibitors,
      );
    } else if (!currentEventId) {
      loadSequence.current += 1;
      setEvent(null);
      setSessions([]);
      setSpeakers([]);
      setPapers([]);
      setExhibitors([]);
      setFeaturedSponsors([]);
      setLoading(false);
    }
  }, [
    currentEventId, areModulesReady, isHomeReady, isLegacyHome,
    agendaEnabled, speakersEnabled, loadSessions, loadSpeakers, loadPapers, loadExhibitors,
  ]);

  useEffect(() => {
    if (!currentEventId || !usesFeaturedSponsorSection) {
      setFeaturedSponsors([]);
      return undefined;
    }
    let active = true;
    void partnerService.getSponsors(currentEventId)
      .then((directory) => {
        if (!active) return;
        setFeaturedSponsors(directory.sponsors
          .filter((sponsor) => sponsor.is_featured)
          .sort((left, right) => (left.featured_order ?? Number.MAX_SAFE_INTEGER) - (right.featured_order ?? Number.MAX_SAFE_INTEGER)));
      })
      .catch(() => { if (active) setFeaturedSponsors([]); });
    return () => { active = false; };
  }, [currentEventId, usesFeaturedSponsorSection]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  async function loadData(eventId: string, loadAgenda: boolean, loadSpeakers: boolean, loadPapers: boolean, loadExhibitors: boolean) {
    const requestSequence = ++loadSequence.current;
    setLoading(true);
    setEvent(null);
    setSessions([]);
    setSpeakers([]);
    setPapers([]);
    setExhibitors([]);
    try {
      // The event itself is required; collections are optional enhancements.  Load
      // them independently so an unavailable endpoint cannot blank the Home screen.
      const ev = await load(`event:${eventId}`, () => eventService.getById(eventId));
      if (requestSequence !== loadSequence.current) return;
      // Render the event-owned blocks immediately. Optional collections can be slow
      // or unavailable, and should only affect their own home sections.
      setEvent(ev);
      const [sessionsResult, speakersResult, papersResult, exhibitorsResult] = await Promise.allSettled([
        loadAgenda
          ? load(`sessions:${eventId}`, () => sessionService.getAll(eventId))
          : Promise.resolve([]),
        loadSpeakers
          ? load(`speakers:${eventId}`, () => speakerService.getAll(eventId))
          : Promise.resolve([]),
        loadPapers
          ? load(`papers:${eventId}`, () => paperService.getAll(eventId))
          : Promise.resolve([]),
        loadExhibitors
          ? load(`exhibitors:${eventId}`, () => exhibitorService.getAll(eventId))
          : Promise.resolve([]),
      ]);
      if (requestSequence !== loadSequence.current) return;

      const valueOrEmpty = <T,>(result: PromiseSettledResult<T[]>): T[] =>
        result.status === 'fulfilled' ? result.value : [];
      const all = valueOrEmpty(sessionsResult);
      const spk = valueOrEmpty(speakersResult);
      const allPapers = valueOrEmpty(papersResult);
      const allExhibitors = valueOrEmpty(exhibitorsResult);
      const featuredPartners = allExhibitors
        .filter((partner) => partner.is_featured)
        .sort((left, right) =>
          (left.featured_order ?? Number.MAX_SAFE_INTEGER)
          - (right.featured_order ?? Number.MAX_SAFE_INTEGER));
      const featuredSpeakers = spk
        .filter((speaker) => speaker.is_featured)
        .sort((left, right) =>
          (left.featured_order ?? Number.MAX_SAFE_INTEGER)
          - (right.featured_order ?? Number.MAX_SAFE_INTEGER));
      const featuredPapers = allPapers
        .filter((paper) => paper.is_featured)
        .sort((left, right) =>
          (left.featured_order ?? Number.MAX_SAFE_INTEGER)
          - (right.featured_order ?? Number.MAX_SAFE_INTEGER));

      setSessions(all);
      setSpeakers(featuredSpeakers);
      setPapers(featuredPapers);
      setExhibitors(featuredPartners);
    } catch (err) {
      if (requestSequence !== loadSequence.current) return;
      console.error('Failed to load home data', err);
    } finally {
      if (requestSequence === loadSequence.current) setLoading(false);
    }
  }

  const displayName = profile?.name?.split(' ')[0] ?? 'there';
  const avatarUrl = avatarImageUrl(profile?.avatar_url);
  const { live: liveSessions, upcoming: upcomingSessions } = event
    ? getScheduleState(sessions, event, now)
    : { live: [], upcoming: [] };
  const quickActions = [
    isEnabled('venue_map')
      ? { icon: MapPin, label: labelFor('venue_map'), path: '/map' }
      : null,
    agendaEnabled
      ? { icon: Calendar, label: labelFor('agenda'), path: '/agenda' }
      : null,
    isEnabled('notes')
      ? { icon: FileText, label: labelFor('notes'), path: '/notes' }
      : null,
  ].filter((action): action is NonNullable<typeof action> => action !== null);
  const quickActionColumns = quickActions.length === 1
    ? 'grid-cols-1'
    : quickActions.length === 2
      ? 'grid-cols-2'
      : 'grid-cols-3';
  const homeBlock = (key: typeof homeBlocks[number]['key']) => homeBlocks.find((block) => block.key === key);
  const showHomeBlock = (key: typeof homeBlocks[number]['key']) => isLegacyHome || Boolean(homeBlock(key));
  const homeOrder = (key: typeof homeBlocks[number]['key']) => isLegacyHome ? undefined : homeBlock(key)?.order;
  const homeTitle = (key: typeof homeBlocks[number]['key'], fallback: string) => homeBlock(key)?.title ?? fallback;
  const announcementLimit = homeBlock('announcements')?.limit ?? 3;
  const homeAnnouncements = useMemo(
    () => currentEventId
      ? selectHomeAnnouncements(announcements, currentEventId, now, announcementLimit)
      : [],
    [announcements, currentEventId, now, announcementLimit],
  );

  return (
    <div
      className="size-full flex flex-col overflow-hidden transition-colors duration-300"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      {/* Top App Bar */}
      <div
        className="app-screen-header flex items-center justify-between px-4 py-3 sticky top-0 z-10 shrink-0 border-b backdrop-blur-md transition-colors duration-300"
        style={{ backgroundColor: 'var(--app-nav-bg)', borderColor: 'var(--app-divider)' }}
      >
        <button
          onClick={() => navigate('/profile')}
          className="flex items-center gap-3 active:opacity-70 transition-opacity"
        >
          <div
            className="size-10 rounded-full overflow-hidden border-2"
            style={{ borderColor: 'var(--app-accent-solid)' }}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <span
                aria-label="Profile"
                className="w-full h-full flex items-center justify-center text-sm font-semibold"
                style={{ backgroundColor: 'var(--app-accent-dim)', color: 'var(--app-accent)' }}
              >
                {displayName.charAt(0).toUpperCase() || '?'}
              </span>
            )}
          </div>
          <div>
            <p className="text-[10px] tracking-widest uppercase" style={{ color: 'var(--app-text-muted)' }}>
              {greeting()}
            </p>
            <p className="text-sm" style={{ color: 'var(--app-text)' }}>{displayName}</p>
          </div>
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate('/notifications')}
            className="relative p-2 active:opacity-70 transition-opacity"
            aria-label="Notifications"
          >
            <Bell className="w-6 h-6" style={{ color: 'var(--app-text-subtle)' }} />
            {unreadNotificationCount > 0 && (
              <span
                aria-label={`${unreadNotificationCount} unread notification${unreadNotificationCount === 1 ? '' : 's'}`}
                className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full flex items-center justify-center text-[9px] font-semibold text-white border-2"
                style={{ backgroundColor: 'var(--app-accent-solid)', borderColor: 'var(--app-nav-bg)' }}
              >
                {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="app-nav-content flex-1 overflow-y-auto">
        <div className="flex flex-col gap-5 p-4">

          {customSections.map((section) => (
            <CustomHomeSectionCard key={section.id} section={section} featuredSponsors={featuredSponsors} />
          ))}

          {/* Featured Event Card */}
          {showHomeBlock('event_hero') && <div
            className="rounded-xl overflow-hidden border"
            style={{
              backgroundColor: 'var(--app-surface)',
              borderColor: 'var(--app-border)',
              boxShadow: 'var(--app-card-shadow)',
              order: homeOrder('event_hero'),
            }}
          >
            <div className="p-5">
              <p className="text-xs tracking-widest uppercase mb-1" style={{ color: 'var(--app-accent)' }}>
                {homeTitle('event_hero', 'Event')}
              </p>
              {!event ? (
                <div className="h-7 w-48 rounded animate-pulse" style={{ background: 'var(--app-muted)' }} />
              ) : (
                <h2 className="text-2xl" style={{ color: 'var(--app-text)' }}>{event.name}</h2>
              )}
              <div className="flex flex-col gap-2 mt-3">
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--app-text-muted)' }}>
                  <Calendar className="w-3.5 h-3.5 shrink-0" />
                  {event ? (
                    <span>{formatEventDateRange(event.date_start, event.date_end)}</span>
                  ) : (
                    <div className="h-4 w-32 rounded animate-pulse" style={{ background: 'var(--app-muted)' }} />
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--app-text-muted)' }}>
                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                  {event ? (
                    <span>{event.location}</span>
                  ) : (
                    <div className="h-4 w-40 rounded animate-pulse" style={{ background: 'var(--app-muted)' }} />
                  )}
                </div>
              </div>
            </div>
          </div>}

          {/* Quick Access */}
          {showHomeBlock('quick_actions') && (isLegacyHome ? quickActions.length > 0 && (
          <div className={`grid ${quickActionColumns} gap-3`} style={{ order: homeOrder('quick_actions') }}>
            {quickActions.map(({ icon: Icon, label, path }) => (
              <button
                key={label}
                onClick={() => navigate(path)}
                className="rounded-xl py-4 flex flex-col items-center gap-2 border active:scale-95 transition-transform"
                style={{
                  backgroundColor: 'var(--app-surface)',
                  borderColor: 'var(--app-border)',
                  boxShadow: 'var(--app-card-shadow)',
                }}
              >
                <Icon className="w-6 h-6" style={{ color: 'var(--app-accent)' }} />
                <span className="text-xs text-center" style={{ color: 'var(--app-text)' }}>{label}</span>
              </button>
            ))}
          </div>
          ) : <section style={{ order: homeOrder('quick_actions') }}>
            <h3 className="mb-3 text-lg font-semibold tracking-tight" style={{ color: 'var(--app-text)' }}>{homeTitle('quick_actions', 'Quick access')}</h3>
            <HomeQuickActions moduleKeys={homeBlock('quick_actions')?.module_keys ?? []} labelFor={labelFor} onNavigate={navigate} />
          </section>)}

          {showHomeBlock('announcements') && !loading && homeAnnouncements.length > 0 && (
            <section style={{ order: homeOrder('announcements') }}>
              <h3 className="mb-3 text-lg font-semibold tracking-tight" style={{ color: 'var(--app-text)' }}>{homeTitle('announcements', 'Announcements')}</h3>
              <div className="space-y-2">
                {homeAnnouncements.map((announcement) => (
                  <button key={announcement.id} onClick={() => navigate('/notifications')} className="w-full rounded-xl border p-3 text-left" style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}>
                    <p className="text-[15px] font-semibold leading-snug" style={{ color: 'var(--app-text)' }}>{announcement.title}</p>
                    {announcement.message && <p className="mt-1 line-clamp-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>{announcement.message}</p>}
                    <time dateTime={announcement.created_at} className="mt-2 block text-[11px]" style={{ color: 'var(--app-text-subtle)' }}>
                      {new Date(announcement.created_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                      })}
                    </time>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Happening Now */}
          {agendaEnabled && (showHomeBlock('happening_now') || showHomeBlock('up_next')) && (
          <>
          {showHomeBlock('happening_now') && <div style={{ order: homeOrder('happening_now') }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold tracking-tight" style={{ color: 'var(--app-text)' }}>{homeTitle('happening_now', 'Happening Now')}</h3>
                {liveSessions.length > 0 && (
                  <span className="size-2 bg-[#ef4444] rounded-full" />
                )}
              </div>
              <button
                onClick={() => navigate('/agenda')}
                className="text-xs font-medium active:opacity-70"
                style={{ color: 'var(--app-accent)' }}
              >
                See all
              </button>
            </div>

            {loading ? (
              <div className="flex gap-3">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex-none w-[260px] h-40 rounded-xl animate-pulse"
                    style={{ background: 'var(--app-surface)' }}
                  />
                ))}
              </div>
            ) : liveSessions.length === 0 ? (
              <div
                className="rounded-xl p-4 border text-center"
                style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
              >
                <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>No sessions live right now</p>
                <p className="text-xs mt-1" style={{ color: 'var(--app-text-subtle)' }}>Check the full agenda for upcoming sessions</p>
              </div>
            ) : (
              <ul aria-label={homeTitle('happening_now', 'Happening Now')} className="space-y-2">
                {liveSessions.map((session) => {
                  const speaker = session.speakers?.[0]?.profile;
                  return (
                    <li key={session.id}>
                      <button
                        aria-label={session.title}
                        onClick={() => navigate(`/session/${session.id}`)}
                        className="w-full rounded-xl border p-3.5 flex items-center gap-3 text-left active:scale-[0.98] transition-transform"
                        style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-accent-border)', boxShadow: 'var(--app-card-shadow)' }}
                      >
                        <span className="size-11 shrink-0 rounded-lg flex flex-col items-center justify-center gap-1" style={{ background: 'var(--app-accent-dim)', color: 'var(--app-accent)' }}>
                          <span className="size-2 rounded-full bg-[#ef4444]" />
                          <span className="text-[10px] font-medium">Live</span>
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[15px] font-semibold leading-snug line-clamp-2" style={{ color: 'var(--app-text)' }}>{session.title}</span>
                          <span className="mt-1 block text-xs leading-relaxed" style={{ color: 'var(--app-text-muted)' }}>
                            {timeLabel(session)}{session.room ? `, ${session.room}` : ''}
                          </span>
                          {speaker && <span className="block text-[11px] leading-relaxed line-clamp-1" style={{ color: 'var(--app-text-subtle)' }}>{speaker.name}</span>}
                        </span>
                        <ChevronRight className="size-4 shrink-0" style={{ color: 'var(--app-text-subtle)' }} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>}

          {/* Up Next */}
          {showHomeBlock('up_next') && <div style={{ order: homeOrder('up_next') }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold tracking-tight" style={{ color: 'var(--app-text)' }}>{homeTitle('up_next', 'Up Next')}</h3>
              <button
                onClick={() => navigate('/agenda')}
                className="text-xs font-medium active:opacity-70"
                style={{ color: 'var(--app-accent)' }}
              >
                See all
              </button>
            </div>

            {loading ? (
              <div className="flex flex-col gap-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-16 rounded-xl animate-pulse" style={{ background: 'var(--app-surface)' }} />
                ))}
              </div>
            ) : upcomingSessions.length === 0 ? (
              <div
                className="rounded-xl p-4 border text-center"
                style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
              >
                <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>No upcoming sessions</p>
              </div>
            ) : (
              <ul aria-label={homeTitle('up_next', 'Up Next')} className="space-y-2">
                {upcomingSessions.map((session) => {
                  const fav = isFavorite(session.id, 'session');
                  return (
                    <li key={session.id} className="rounded-xl border p-3.5 flex items-center gap-2" style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-border)', boxShadow: 'var(--app-card-shadow)' }}>
                      <button
                        aria-label={session.title}
                        onClick={() => navigate(`/session/${session.id}`)}
                        className="min-w-0 flex flex-1 items-center gap-3 text-left active:opacity-70"
                      >
                        <span className="size-11 shrink-0 rounded-lg flex flex-col items-center justify-center gap-0.5" style={{ background: 'var(--app-accent-dim)', color: 'var(--app-accent)' }}>
                          <Clock className="size-4" />
                          <span className="text-[9px] font-medium">{timeLabel(session).replace(':00', '')}</span>
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[15px] font-semibold leading-snug line-clamp-2" style={{ color: 'var(--app-text)' }}>{session.title}</span>
                        <span className="mt-1 block text-xs leading-relaxed line-clamp-1" style={{ color: 'var(--app-text-muted)' }}>
                          {[session.room, session.track].filter(Boolean).join(', ')}
                        </span>
                        </span>
                      </button>
                      <button
                        aria-label={fav ? `Remove ${session.title} from saved sessions` : `Save ${session.title}`}
                        onClick={() => {
                          toggleFavorite(session.id, 'session');
                          if (!fav) notify.custom.sessionSaved();
                        }}
                        className="p-2 active:opacity-70 shrink-0"
                      >
                        <Bookmark
                          className="w-4 h-4"
                          style={{ color: fav ? 'var(--app-accent)' : 'var(--app-text-subtle)' }}
                          fill={fav ? 'var(--app-accent)' : 'none'}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>}
          </>
          )}

          {/* Featured Speakers */}
          {showHomeBlock('featured_speakers') && speakersEnabled && !loading && speakers.length > 0 && (
            <div style={{ order: homeOrder('featured_speakers') }}>
              <HomeFeaturedCollection kind="featured_speakers" title={homeTitle('featured_speakers', `Featured ${labelFor('speakers')}`)} items={speakers} limit={homeBlock('featured_speakers')?.limit ?? 5} onNavigate={navigate} onSeeAll={() => navigate('/explore/speakers')} />
            </div>
          )}

          {showHomeBlock('featured_papers') && !loading && (
            <div style={{ order: homeOrder('featured_papers') }}>
              <HomeFeaturedCollection kind="featured_papers" title={homeTitle('featured_papers', `Featured ${labelFor('papers')}`)} items={papers} limit={homeBlock('featured_papers')?.limit ?? 3} onNavigate={navigate} onSeeAll={() => navigate('/explore/papers')} />
            </div>
          )}

          {showHomeBlock('featured_exhibitors') && !loading && (
            <div style={{ order: homeOrder('featured_exhibitors') }}>
              <HomeFeaturedCollection kind="featured_exhibitors" title={homeTitle('featured_exhibitors', `Featured ${labelFor('exhibitors')}`)} items={exhibitors} limit={homeBlock('featured_exhibitors')?.limit ?? 3} onNavigate={navigate} onSeeAll={() => navigate('/explore/exhibitors')} />
            </div>
          )}

          {/* Conference Recap */}
          {showHomeBlock('recap') && isEnabled('recap') && isEnabled('ai_copilot') && (
          <button
            onClick={() => isCurrentEventMember ? navigate('/recap') : navigate('/my-conferences')}
            className="rounded-xl p-4 flex items-center gap-4 active:scale-[0.98] transition-transform border relative overflow-hidden"
            style={{
              background: 'var(--app-surface)',
              borderColor: isCurrentEventMember ? 'var(--app-accent-border)' : 'var(--app-border)',
              boxShadow: isCurrentEventMember ? '0 4px 16px var(--app-accent-glow)' : 'none',
              opacity: isCurrentEventMember ? 1 : 0.6,
              order: homeOrder('recap'),
            }}
          >
            <div
              className="size-12 rounded-full flex items-center justify-center shrink-0"
              style={{ background: 'var(--app-accent-dim)' }}
            >
              {isCurrentEventMember
                ? <Sparkles className="w-6 h-6" style={{ color: 'var(--app-accent)' }} />
                : <Lock className="w-5 h-5" style={{ color: 'var(--app-text-subtle)' }} />}
            </div>
            <div className="flex-1 text-left">
              <p className="text-[15px] font-semibold leading-snug" style={{ color: 'var(--app-text)' }}>{homeTitle('recap', labelFor('recap'))}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                {isCurrentEventMember
                  ? (isEnabled('ai_copilot') ? 'AI summary with action items & follow-ups' : 'Review event highlights and follow-ups')
                  : 'Join conference to unlock the event recap'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 shrink-0" style={{ color: isCurrentEventMember ? 'var(--app-accent)' : 'var(--app-text-subtle)' }} />
          </button>
          )}

          {/* AI Copilot Promo */}
          {isEnabled('ai_copilot') && (
          <button
            onClick={() => isCurrentEventMember ? navigate('/report-builder') : navigate('/my-conferences')}
            className="rounded-xl p-4 flex items-center gap-4 active:scale-[0.98] transition-transform"
            style={{
              background: isCurrentEventMember
                ? 'linear-gradient(135deg, var(--app-accent-solid) 0%, var(--app-accent) 100%)'
                : 'var(--app-surface)',
              boxShadow: isCurrentEventMember ? '0 8px 24px var(--app-accent-glow)' : 'none',
              border: isCurrentEventMember ? 'none' : '1px solid var(--app-border)',
              opacity: isCurrentEventMember ? 1 : 0.6,
              // This cross-event entry point intentionally sits outside the
              // configured event blocks, after their ordered layout.
              order: isLegacyHome ? undefined : 1000,
            }}
          >
            <div className={`size-12 rounded-full flex items-center justify-center shrink-0 ${isCurrentEventMember ? 'bg-white/15' : ''}`}
              style={!isCurrentEventMember ? { background: 'var(--app-accent-dim)' } : {}}>
              {isCurrentEventMember ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" />
                </svg>
              ) : (
                <Lock className="w-5 h-5" style={{ color: 'var(--app-text-subtle)' }} />
              )}
            </div>
            <div className="flex-1 text-left">
              <p className="text-[15px] font-semibold leading-snug" style={{ color: isCurrentEventMember ? 'white' : 'var(--app-text)' }}>{labelFor('ai_copilot')}</p>
              <p className="text-xs mt-0.5" style={{ color: isCurrentEventMember ? 'rgba(255,255,255,0.8)' : 'var(--app-text-muted)' }}>
                {isCurrentEventMember ? 'Generate reports, take smart notes' : 'Join conference to unlock AI features'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 shrink-0" style={{ color: isCurrentEventMember ? 'rgba(255,255,255,0.8)' : 'var(--app-text-subtle)' }} />
          </button>
          )}

          {/* Loading spinner overlay when no event selected */}
          {!currentEventId && !loading && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--app-accent)' }} />
              <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
                No event selected —{' '}
                <button
                  className="underline"
                  style={{ color: 'var(--app-accent)' }}
                  onClick={() => navigate('/event-selector')}
                >
                  choose one
                </button>
              </p>
            </div>
          )}

        </div>
      </div>

      <BottomNav />
    </div>
  );
}
